<script setup>
import { translate } from '@/plugins/i18n'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { useCustomerStore } from '@/stores/customer'
import { elb } from '@elbwalker/walker.js'
import { storeToRefs } from 'pinia'
import { defineEmits, onMounted, ref } from 'vue'

const emit = defineEmits(['success', 'showLogin'])
const customerStore = useCustomerStore()
const { customerPresets } = storeToRefs(customerStore)

const loading = ref(true)
const submitting = ref(false)
const code = ref('')
const qrDataUrl = ref('')
const secretFormatted = ref('')
const form = ref(null)
const el = ref(null)
const qrCanvas = ref(null)

const rules = [
  (value) => value?.length === 6 || translate('validation_msg_min', 6)
]

onMounted(async () => {
  elb(WALKER_COMMANDS.init, el.value)

  // If no credentials in store (e.g. hard reload), redirect to login
  if (!customerPresets.value?.email || !customerPresets.value?.password) {
    emit('showLogin')
    return
  }

  try {
    const { qr_uri, secret } = await customerStore.setupTwoFactor()
    secretFormatted.value = secret
    loading.value = false

    // Wait for next tick so canvas is rendered
    await new Promise((resolve) => setTimeout(resolve, 0))

    // Dynamically import qrcode and render to canvas
    const QRCode = await import('qrcode')
    const toCanvas = QRCode.toCanvas || QRCode.default?.toCanvas
    await toCanvas(qrCanvas.value, qr_uri, { width: 200 })
  } catch (e) {
    console.error('Failed to load 2FA setup', e)
    loading.value = false
  }
})

async function confirmSetup() {
  const { valid } = await form.value.validate()
  if (!valid) return

  submitting.value = true
  try {
    await customerStore.confirmTwoFactorSetup({ code: code.value })
    emit('success')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    ref="el"
    class="d-flex flex-column align-center"
    data-elb="account"
    data-elb-account="action:two_factor_setup"
    data-elbaction="visible"
  >
    <VProgressCircular v-if="loading" indeterminate color="primary" />

    <template v-else>
      <p class="text-center mb-4 font-weight-light l-spacing-1">
        {{ translate('two_factor_setup_scan_qr') }}
      </p>

      <canvas ref="qrCanvas" width="200" height="200" class="mb-4" />

      <p class="text-caption text-center mb-2">
        {{ translate('two_factor_setup_manual_key') }}
      </p>
      <code class="text-body-2 pa-2 bg-grey-lighten-3 rounded mb-4">{{
        secretFormatted
      }}</code>

      <VForm
        ref="form"
        @submit.prevent="confirmSetup"
        lazy-validation
        class="w-100 d-flex flex-column mt-4"
      >
        <VOtpInput
          v-model="code"
          :rules="rules"
          :length="6"
          type="number"
          @finish="confirmSetup"
        />
        <VBtn
          type="submit"
          color="primary"
          class="mt-3"
          :loading="submitting"
          :disabled="submitting"
          depressed
          block
        >
          {{ translate('two_factor_setup_confirm') }}
        </VBtn>
      </VForm>
    </template>
  </div>
</template>
